// src/pages/Users/Techniques/components/TechniqueSheet.jsx

import React, { useEffect } from "react";
import {
  FaExternalLinkAlt,
  FaTimes,
  FaVideo,
  FaVolumeUp,
} from "react-icons/fa";

import { toAbsoluteTechniquePhotoUrl } from "../../../../services/techniquesPhotoServices";

import useTechniqueSpeech from "../hooks/useTechniqueSpeech";
import { speakJapanese } from "../utils/speechUtils";
import {
  FALLBACK_TECHNIQUE_IMG,
  beltTheme,
  cleanTechniqueText,
} from "../utils/techniquesUtils";

function onImageError(event) {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_TECHNIQUE_IMG;
  }
}

export default function TechniqueSheet({ technique, onClose }) {
  const {
    image1_url,
    nom,
    traduction,
    famille,
    ceinture,
    video_url,
    uv2,
    information,
  } = technique || {};

  const cleanInformation = cleanTechniqueText(information);

  const imgSrc =
    toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_TECHNIQUE_IMG;

  const theme = beltTheme(ceinture);
  const speechText = useTechniqueSpeech(nom);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const openVideo = () => {
    if (!video_url) return;

    window.open(video_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/65 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Fiche technique ${nom || ""}`}
    >
      <article
        className="absolute inset-x-0 bottom-0 max-h-[94dvh] overflow-hidden rounded-t-[2rem] bg-white shadow-2xl dark:bg-slate-900 sm:relative sm:inset-auto sm:w-full sm:max-w-3xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header
          className={`relative overflow-hidden bg-gradient-to-r ${theme.gradient} px-4 py-4 text-white`}
        >
          <div className="pointer-events-none absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/20 blur-2xl" />

          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ring-1 ${theme.badge}`}
              >
                {ceinture || "Ceinture"}
              </span>

              <h2 className="mt-2 line-clamp-2 text-xl font-bold leading-tight">
                {nom}
              </h2>

              {traduction && (
                <p className="mt-1 text-sm italic text-white/85">
                  {traduction}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <div className="max-h-[calc(94dvh-128px)] overflow-y-auto p-4">
          <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="aspect-[551/617] w-full">
              <img
                src={imgSrc}
                alt={nom || "Technique de judo"}
                className="h-full w-full object-contain p-2"
                onError={onImageError}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            {famille && (
              <InfoCard label="Famille" value={cleanTechniqueText(famille)} />
            )}

            {ceinture && (
              <InfoCard label="Ceinture" value={cleanTechniqueText(ceinture)} />
            )}

            {/* {uv2 && (
              <InfoCard
                label="Fiche UV2"
                value={
                  Array.isArray(uv2)
                    ? uv2.map(cleanTechniqueText).join(", ")
                    : cleanTechniqueText(uv2)
                }
              />
            )} */}
          </div>

          {cleanInformation && (
            <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Informations
              </p>

              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700 dark:text-slate-300">
                {cleanInformation}
              </p>
            </div>
          )}

          <div className="sticky bottom-0 -mx-4 mt-5 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => speakJapanese(speechText)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaVolumeUp />
                Prononcer
              </button>

              {video_url ? (
                <button
                  type="button"
                  onClick={openVideo}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
                >
                  <FaVideo />
                  Vidéo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
                >
                  <FaExternalLinkAlt />
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}
